import { QUESTION_TYPES } from '@api/instant_survey';

import { toast } from '@utilities';

/**
 * Checks to see if any question logic needs to be changed
 * @param {Object} options.originalQuestion
 * @param {Object} options.question
 * @param {Object} options.questionLogic
 * @param {Object[]} options.questions
 */
export const cleanupLogicAfterEdit = ({ originalQuestion, question, questionLogic, questions }) => {
  let hasLogicChanges = false;
  const modifiedQuestionLogic = { ...questionLogic };

  // recommended question became a custom question, treat it as a removal
  if (originalQuestion.id !== question.id)
    return cleanupLogicAfterRemoval({
      question: originalQuestion,
      questionId: originalQuestion.id,
      questionLogic,
      questions,
    });

  const disqualifyLogic = modifiedQuestionLogic[question.id]?.disqualify;

  if (disqualifyLogic) {
    if (
      [QUESTION_TYPES.ESSAY.value, QUESTION_TYPES['RANK-DRAGDROP'].value].includes(question.type)
    ) {
      // cannot disqualify if the question type is essay or rank
      delete modifiedQuestionLogic[question.id].disqualify;
      hasLogicChanges = true;
    } else if (disqualifyLogic.answers.length > 0) {
      // clear answers to prevent prior selections from 'sticking'

      if (shouldClearQuestionLogic(question, originalQuestion)) {
        modifiedQuestionLogic[question.id].disqualify.answers = [];
        hasLogicChanges = true;
      }
    }
  }

  Object.keys(modifiedQuestionLogic).forEach((id) => {
    const skipLogic = modifiedQuestionLogic[id].skip?.logic;
    if (skipLogic) {
      skipLogic.forEach((row) => {
        if (row.question?.id === question.id) {
          if (!shouldClearQuestionLogic(question, row.question)) {
            return;
          }

          row.answers = [];
          row.question = [
            QUESTION_TYPES.ESSAY.value,
            QUESTION_TYPES.NUMERIC.value,
            QUESTION_TYPES['RANK-DRAGDROP'].value,
          ].includes(question.type)
            ? undefined
            : question;
          hasLogicChanges = true;
        }
      });
    }
  });

  if (hasLogicChanges) displayQuestionLogicUpdatedToast();
  return modifiedQuestionLogic;
};

const shouldClearQuestionLogic = (question, originalQuestion) => {
  if (question.type !== originalQuestion.type) {
    return true;
  }

  // Check if any selectable options were removed/edited
  return originalQuestion.answers.some(({ id: originalId, value: originalValue }) => {
    const matchingNewOption = question.answers.find(
      ({ id, value }) => originalId === id && originalValue === value
    );
    return !matchingNewOption;
  });
};

/**
 * Checks to see if any question logic needs to be changed
 * @param {Object} options.question
 * @param {Number} options.questionId
 * @param {Object} options.questionLogic
 * @param {Object[]} options.questions
 */
export const cleanupLogicAfterRemoval = ({ question, questionId, questionLogic, questions }) => {
  let hasLogicChanges = false;
  const modifiedQuestionLogic = { ...questionLogic };
  const questionIdsToRemove = new Set([
    String(questionId),
    ...(question?.type === QUESTION_TYPES.SEQUENTIAL_MONADIC.value
      ? (question.subQuestions || []).map(({ id }) => String(id))
      : []),
  ]);

  // remove logic for question that was removed
  questionIdsToRemove.forEach((idToRemove) => {
    if (modifiedQuestionLogic[idToRemove]?.disqualify || modifiedQuestionLogic[idToRemove]?.skip) {
      delete modifiedQuestionLogic[idToRemove];
      hasLogicChanges = true;
    }
  });

  Object.keys(modifiedQuestionLogic).forEach((id) => {
    const skipLogic = modifiedQuestionLogic[id].skip?.logic;
    if (skipLogic) {
      // if the removal of a question moved this question to the first postion, remove skip logic
      if (questions.findIndex((question) => String(id) === String(question.id)) === 0) {
        delete modifiedQuestionLogic[id]?.skip;
        hasLogicChanges = true;
        return;
      }

      // remove any skip logic that references the removed question
      // Check if the question has an id, as there are some cases the logic is for quota groups not questions
      const updatedSkipLogic = skipLogic.filter(
        (row) => !questionIdsToRemove.has(String(row.question?.id))
      );
      if (updatedSkipLogic.length !== skipLogic.length) {
        if (updatedSkipLogic.length === 0) {
          delete modifiedQuestionLogic[id].skip;
        } else {
          modifiedQuestionLogic[id].skip.logic = updatedSkipLogic;
        }
        hasLogicChanges = true;
      }
    }
  });

  if (hasLogicChanges) displayQuestionLogicUpdatedToast();
  return modifiedQuestionLogic;
};

const displayQuestionLogicUpdatedToast = () => {
  toast(
    <p>
      Some logic in this questionnaire has been updated or removed due to a survey question change.
    </p>
  );
};

/**
 * Syncs answer piping references with updated question numbers
 * @param {String} value
 * @param {Number} oldQuestionNumber
 * @param {Number} newQuestionNumber
 * @returns {String}
 */
export const replacePipingQuestionReference = (value, oldQuestionNumber, newQuestionNumber) => {
  if (typeof value !== 'string') {
    return value;
  }

  return value.replace(/answer from q(\d+)/g, (match, questionNumber) =>
    Number(questionNumber) === oldQuestionNumber ? `answer from q${newQuestionNumber}` : match
  );
};
